// ABOUTME: The split CC pair (SPEC-26 D4/AC-3b/AC-7) — two independent leaf subscribers, one per
// ABOUTME: sender, composed into the Figma footer bar as a 3-column row (R.2: user | particles | agent).

import React, { useEffect, useRef } from 'react';
import { useTranscriptBySender } from '../../../state/transcriptStore';
import { useVoicePacedReveal, useSmoothReveal } from './captionReveal';
import { RAIL_UNIT, u } from '../../ReelChrome/railGeometry';
import { BARE } from '../../VoiceFirst/VoiceDock/voiceDockTokens'; // SPEC-82 bare_footer

// Bar geometry — node 45:1146 / 45:2281, authored at 2138.667x192 at (850.67, 1872) in a 3840x2160
// frame. Percentages so the bar tracks the composition instead of being pinned to one resolution;
// Phase 4 shipped it at a fixed 64px min-height, which did not scale at all (R.3 defect 7).
export const BAR_W_PCT = (2138.667 / 3840) * 100;   // 55.69%
export const BAR_H_PCT = (150 / 2160) * 100;         // 8.89%
export const BAR_BOTTOM_PCT = ((2160 - 2064) / 2160) * 100; // 4.44%
const BAR_PAD_X = 48;      // 2138.667 - (1586.667 + 504) — symmetric either side
export const CLUSTER_W = 140; // node 45:1147, the widest authored cluster (139.69)
const BAR_RADIUS = 26.667; // reel.radius.ccBarDesktop
// Scale factor for caption bar outer box padding/radius (2026-08-28): 0.70/0.80 = 0.875
const BAR_OUTER_SCALE = 0.875;
const CAPTION_FONT = 37.333; // reel.text.tagBody — the same absolute px on BOTH compositions
// Gutter reserved between the particle cluster and the agent's text column (JC 2026-08-18).
// The row layout has no gap between the three columns by default — the agent column's flex-1
// area starts flush against the cluster's right edge, so a 2-line reply that needs close to its
// full column width visually touches the cluster. This margin (on the LEFT of the agent column,
// i.e. the cluster side) reserves clearance there without moving the column's right edge, which
// stays anchored to BAR_PAD_X same as before. Tune after an on-screen check; not an authored
// Figma measurement.
const AGENT_CLUSTER_GUTTER = 60;

// SPEC-26 Phase 4b — the mobile bar (node 45:2605 / 45:3306 / 45:3188, identical on all 7 frames)
// is the same frosted pill with a completely different interior: the particle cluster sits hard
// LEFT rather than centred, and the two captions stack (guest above, agent below) instead of
// occupying the outer columns of a 3-up row. Positions are authored inside the bar's own 1009x247
// box, so they are relative to it, not to the 1216 frame.
const MOBILE = {
  radius: 37,          // reel.radius.ccBarMobile — NOT the desktop bar's 26.667
  clusterLeft: 33.5,   // node 45:3307
  clusterW: 139.69,
  textLeft: 207,       // node 45:3357 / 45:3358 — both caption nodes share this left edge
  padRight: 42,        // 1009 - (207 + 760)
};
// The gap between the cluster's right edge and the text column's left edge, so the two authored
// left offsets stay the source of truth rather than being re-derived at the call site.
MOBILE.textGap = MOBILE.textLeft - MOBILE.clusterLeft - MOBILE.clusterW; // 33.81
// SPEC-82 review L2: a widened cluster column (the glow ring) eats the authored gap to the text, and 176 authored px leaves none —
// the ring's stroke touched the caption. The gap never goes below this (the ring's own glow blur, authored px); the default
// cluster overhangs by 0, so every flag-off render keeps the authored 33.81.
const MOBILE_MIN_TEXT_GAP = 28;

// Each sub-component is hardcoded to one sender — never a shared component switched by a `sender`
// prop — so a barge-in (a new user message arriving) can only ever re-render the User leaf; the
// Agent leaf's own subscription is untouched (SPEC-26 D4: "independent", not "supersede").

// Figma puts the guest's words in the left column and the agent's reply in the right, with the
// particle cluster centred between them (45:2283 sits at exactly the bar's midpoint). These leaves
// own only their own text treatment — the bar and its columns belong to the container, so neither
// leaf can drift out of alignment with the other.
// Longer replies always exist, but the bar has a fixed authored height. A static 2-line clamp
// froze on the FIRST two lines of a growing streamed reply — later words kept arriving off the
// bottom of the box, invisible, so the caption looked stuck. Capping the box to 2 lines' worth of
// height and auto-scrolling it to the bottom on every text update keeps the latest words in view,
// the way a live caption/subtitle track behaves, instead of clipping the reply's start forever.
const CAPTION_LINE_HEIGHT = 1.15;
const CAPTION_VISIBLE_LINES = 2;
// Real-px floor on the caption type (2026-09-03). The mobile unit set the transcript — the one
// surface a guest actually READS — at 11.5px on a 375px phone and 9.8px on a 320px one, the only
// mobile text in the chrome with no floor at all. 12 rather than the chrome's 11: this is body
// copy, not a label. The desktop unit's own floor already puts this at 16px, so nothing changes
// there; on a mainstream 390-440px handset the authored size is 12-13.5px and the floor is inert.
export const CAPTION_MIN_PX = 12;
const captionFontSize = () => `max(${CAPTION_MIN_PX}px, ${u(CAPTION_FONT)})`;
const captionMaxHeight = () => `calc(${captionFontSize()} * ${CAPTION_LINE_HEIGHT * CAPTION_VISIBLE_LINES})`;

// Shared by both leaves so the guest's and the agent's captions wrap, clip and scroll identically
// — the only difference between them is typographic (font weight/family), not layout behaviour.
function ScrollingCaption({ text, className }) {
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [text]);

  return (
    <div ref={scrollRef} className="overflow-y-hidden" style={{ maxHeight: captionMaxHeight() }}>
      <p
        className={`m-0 text-reel-chrome-textPrimary pointer-events-none ${className}`}
        style={{ fontSize: captionFontSize(), lineHeight: CAPTION_LINE_HEIGHT }}
      >
        {text}
      </p>
    </div>
  );
}

// The two leaves reveal at DIFFERENT paces, on purpose — see `captionReveal.js`. The guest's words
// are already spoken by the time their ASR text arrives, so theirs is smoothed, never delayed; the
// agent's arrives ahead of its own voice, so theirs is held back to the audio clock.
function CaptionOverlayUser() {
  const message = useTranscriptBySender('user');
  const heard = useSmoothReveal(message?.text);
  if (!message?.text) return null;
  return <ScrollingCaption text={heard} className="font-reel-medium" />;
}

function CaptionOverlayAgent() {
  const message = useTranscriptBySender('agent');
  const spoken = useVoicePacedReveal(message?.text);
  if (!message?.text) return null;
  return <ScrollingCaption text={spoken} className="font-reel-heading font-bold" />;
}

/**
 * The Figma footer bar (desktop `Frame 84`, nodes 45:1146 / 45:2281): ONE frosted pill laid out as
 * three columns — the guest's words left, the particle cluster centred, the agent's reply right.
 * Phase 4 built it as `[viz][user stacked over agent]`, which is not the design (R.3 defect 5).
 *
 * The bar renders whenever it's enabled — `ReelFooter` only mounts once the intro has finished
 * (App.js gates it on `!showIntro`), so there is no "before the first word" moment where this
 * would paint an empty blurred rectangle over the intro film. Hiding it until the first transcript
 * message arrived (Phase 4's behaviour) instead made the whole footer flicker on mid-conversation,
 * which reads as broken chrome rather than a deliberate empty state.
 *
 * Sizing comes from the parent slot, so `ReelFooter` can flank it with the keyboard and mic icons
 * from outside without the bar drifting off centre.
 *
 * On the mobile composition the same bar is laid out as `[cluster | guest over agent]` — see the
 * MOBILE table above and `MobileFooter`, which owns its outer box the way `ReelFooter` owns the
 * desktop one. Both layouts share these two leaves, and therefore share D4's independent
 * per-sender subscriptions; only the arrangement differs.
 *
 * @param {{
 *   enabled?: boolean,             // the guest's CC toggle (spec §6.3 step 7)
 *   visualizer?: React.ReactNode,  // the particle cluster (node 45:2283 desktop / 45:3307 mobile)
 *   input?: React.ReactNode,       // text-entry field, replaces the user column when typing
 *   layout?: 'row'|'stacked',      // 'row' = desktop 3-column, 'stacked' = mobile
 *   unit?: string,                 // the `--u` value for this composition's authored space
 *   bare?: boolean,                // SPEC-82 bare_footer: no frosted background/blur, caption text-shadow instead
 *   clusterW?: number,             // SPEC-82: authored width of the cluster column (default: the authored one)
 * }} props
 */
function CaptionBar({
  enabled = true, visualizer = null, input = null, layout = 'row', unit = RAIL_UNIT,
  bare = false, clusterW, // SPEC-82: `bare_footer` drops the pill; `clusterW` widens the cluster column for a ring
}) {
  if (!enabled) return null;

  if (layout === 'stacked') {
    return (
      <div
        data-testid="reel-caption-bar"
        className={`w-full h-full flex items-center overflow-hidden${bare ? '' : ' bg-reel-chrome-panel backdrop-blur-reel-chrome'}`}
        style={{
          '--u': unit,
          ...(bare ? { textShadow: BARE.textShadow } : {}),
          paddingLeft: `calc(${MOBILE.clusterLeft * BAR_OUTER_SCALE} * var(--u))`,
          paddingRight: `calc(${MOBILE.padRight * BAR_OUTER_SCALE} * var(--u))`,
          borderRadius: `calc(${MOBILE.radius * BAR_OUTER_SCALE} * var(--u))`,
          // See PointTag.js — the CC bar's blur sits over the same live video and re-renders on
          // every streamed word, so it needs the same compositor isolation.
          transform: 'translateZ(0)',
          willChange: 'backdrop-filter, transform',
          contain: 'paint',
        }}
      >
        {/* Hard left, vertically centred — the mobile plates never centre the cluster. */}
        <div
          className="shrink-0 flex items-center justify-center"
          style={{ width: u(clusterW ?? MOBILE.clusterW), marginRight: u(Math.max(MOBILE_MIN_TEXT_GAP, MOBILE.textGap - ((clusterW ?? MOBILE.clusterW) - MOBILE.clusterW))) }}
        >
          {visualizer}
        </div>

        {/* The guest's line above the agent's, both left-aligned on the same edge (both authored
            text nodes start at x=207). The block is centred in the bar as a whole, which is what
            45:2947's single 3-line caption and 45:3413's 2-line one both do. */}
        <div className="flex-1 min-w-0">
          {input || <CaptionOverlayUser />}
          <CaptionOverlayAgent />
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="reel-caption-bar"
      className={`w-full h-full flex items-center overflow-hidden${bare ? '' : ' bg-reel-chrome-panel backdrop-blur-reel-chrome'}`}
      style={{
        '--u': unit,
        ...(bare ? { textShadow: BARE.textShadow } : {}),
        paddingLeft: `calc(${BAR_PAD_X * BAR_OUTER_SCALE} * var(--u))`,
        paddingRight: `calc(${BAR_PAD_X * BAR_OUTER_SCALE} * var(--u))`,
        borderRadius: `calc(${BAR_RADIUS * BAR_OUTER_SCALE} * var(--u))`,
        // See PointTag.js — the CC bar's blur sits over the same live video and re-renders on
        // every streamed word, so it needs the same compositor isolation.
        transform: 'translateZ(0)',
        willChange: 'backdrop-filter, transform',
        contain: 'paint',
      }}
    >
      {/* The two text columns flex equally and the cluster is fixed, which keeps the cluster on
          the bar's exact midpoint (45:2283 sits at 1069.33 of 2138.67) while letting a sentence
          use the width the design actually gives it. Pinning them to the authored 504px instead
          wrapped "How long is the watch's power reserve?" onto two lines, which the plate does
          not do — the Figma text node is sized to its content, not to a fixed column. */}
      <div className="flex-1 min-w-0">
        {input || <CaptionOverlayUser />}
      </div>

      {/* Centre — the particle cluster, at the bar's exact midpoint. */}
      <div className="shrink-0 flex items-center justify-center" style={{ width: u(clusterW ?? CLUSTER_W) }}>
        {visualizer}
      </div>

      {/* Right column — the agent. FIXED width (not shrink-to-content-then-justify-end): the
          voice-paced reveal changes the text's length continuously as it streams, then jumps to
          the full remaining sentence the instant speech ends (see resetSpeechClock's "release to
          full line" in App.js) — a shrink-to-fit box recalculates its own width on every one of
          those changes and, being right-justified, visibly jumps sideways each time. A constant-
          width column (same shape as the user column on the left) means only the text WRAPS as it
          grows; the box itself never moves. AGENT_CLUSTER_GUTTER reserves the cluster-side margin. */}
      <div className="flex-1 min-w-0 text-left" style={{ paddingLeft: u(AGENT_CLUSTER_GUTTER) }}>
        <CaptionOverlayAgent />
      </div>
    </div>
  );
}

/**
 * SPEC-82: where the centre of the cluster slot sits inside the bar, as a CSS `left` in the bar's own
 * `--u` — so the voice-first dock (which must live OUTSIDE this clipping bar) can sit exactly where the
 * dots do. Desktop: the two text columns flex equally, but the agent column carries
 * AGENT_CLUSTER_GUTTER of left padding, which the flex arithmetic splits across the free space, so the
 * slot centre is half a gutter LEFT of the bar's midpoint. Mobile: the cluster is hard left.
 */
export function clusterSlotLeft(layout, clusterW) {
  if (layout === 'stacked') {
    return `calc(${MOBILE.clusterLeft * BAR_OUTER_SCALE + (clusterW ?? MOBILE.clusterW) / 2} * var(--u))`;
  }
  return `calc(50% - ${AGENT_CLUSTER_GUTTER / 2} * var(--u))`;
}

/** SPEC-82: the cluster column's authored defaults, so a caller widening it knows the overhang. */
export const CLUSTER_DEFAULT_W = { row: CLUSTER_W, stacked: MOBILE.clusterW };

const CaptionOverlay = { User: CaptionOverlayUser, Agent: CaptionOverlayAgent, Bar: CaptionBar };

export default CaptionOverlay;
export { CaptionOverlayUser, CaptionOverlayAgent, CaptionBar };
